import { useNavigate, useSearchParams } from "react-router-dom";
import { useNordigenAccounts } from "../../hooks/use-nordigen";
import { useModal } from "../../hooks/use-modal.jsx";
import Spinner from "../UI/Spinner";
import Callout from "../UI/Callout";
import { ModalDialogFooter } from "../UI/ModalDialog";
import {
  handleAPIError,
  TextOneOrMany,
  UNKNOWN_ERROR_MESSAGE,
} from "../../utils/index.jsx";
import BankCheckbox from "./BankCheckbox";
import { Form, Formik } from "formik";
import { array, object, string } from "yup";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import { useState } from "react";
import { useLinxoAccounts } from "../../hooks/use-linxo";
import { BankProvider, LinxoRedirectStatus } from "../../utils/constants/linxo";

export default function BankSyncForm({ provider = BankProvider.NORDIGEN }) {
  const [searchParams] = useSearchParams();
  const referenceId = searchParams.get("reference_id");
  const connectionCode = searchParams.get("connection_code");
  const connectionId = searchParams.get("connection_id");
  const linxoUsage = searchParams.get("widget_usage");
  const status = searchParams.get("status");
  const linxoSuccess =
    provider === BankProvider.LINXO &&
    [LinxoRedirectStatus.AUTHORIZED, LinxoRedirectStatus.SUCCESS].includes(
      status,
    );
  const {
    accounts: linxoAccounts,
    isLoading: isLoadingLinxoAccounts,
    error: linxoError,
  } = useLinxoAccounts(linxoSuccess, linxoUsage, connectionCode, connectionId);

  let {
    accounts: nordigenAccounts,
    isLoading: isLoadingNordigenAccounts,
    error: nordigenError,
    synchronizeAccounts: synchronizeNordigenAccounts,
  } = useNordigenAccounts(provider === BankProvider.NORDIGEN && referenceId);

  const error = nordigenError || linxoError;

  const [Modal] = useModal(true);
  const nav = useNavigate();

  const [synchronizeError, setSynchronizeError] = useState(null);

  if (isLoadingNordigenAccounts || isLoadingLinxoAccounts) {
    return (
      <Modal size={"medium"}>
        <h2 className={"marginB20"}>Chargement des comptes...</h2>
        <Spinner width={"5em"} />
      </Modal>
    );
  }

  if (error) {
    let errorMessage = UNKNOWN_ERROR_MESSAGE;
    if (error.response?.status === 404) {
      errorMessage =
        "Aucun compte n'a pu être récupéré. Veuillez recommencer la connexion à votre banque.";
    } else if (error.response?.status === 400) {
      errorMessage =
        "La requête est invalide. Veuillez recommencer la connexion à votre banque.";
    } else if (status === LinxoRedirectStatus.FAILED) {
      errorMessage =
        "Echec de la connexion à votre banque. Veuillez réessayer.";
    }
    return (
      <Modal size={"medium"}>
        <h2>Erreur</h2>
        <Callout variant="danger">{errorMessage}</Callout>
      </Modal>
    );
  }

  async function submitHandler(values) {
    setSynchronizeError(null);
    try {
      if (provider === BankProvider.NORDIGEN) {
        await synchronizeNordigenAccounts(values.accounts);
        nav("../post-sync", {
          state: { accounts: values.accounts },
          replace: true,
        });
      } else {
        nav("..");
      }
    } catch (e) {
      setSynchronizeError(handleAPIError(e));
    }
  }

  const accounts =
    provider === BankProvider.NORDIGEN ? nordigenAccounts : linxoAccounts;

  // Preselect accounts that have a status i.e. that are already synced with Qalimo
  const initialValues = {
    accounts: accounts
      .filter(
        (account) => account.status !== null && account.status !== undefined,
      )
      .map((account) => account.unique_id),
  };

  if (accounts.length === 0) {
    return (
      <Modal size={"medium"}>
        <h2>Aucun compte supporté</h2>
        <Callout variant="danger">
          Aucun compte supporté n'a pu être récupéré. Veuillez contacter le
          support Qalimo.
        </Callout>
      </Modal>
    );
  }

  return (
    <Modal size={"medium"}>
      <Formik
        initialValues={initialValues}
        validationSchema={object({ accounts: array().of(string()) })}
        onSubmit={submitHandler}
      >
        {({ values }) => (
          <Form>
            <h2>Comptes récupérés</h2>
            {provider === BankProvider.NORDIGEN && (
              <p>
                Les comptes suivants ont pu être récupérés de votre banque.
                Veuillez sélectionner tous ceux que vous souhaitez synchroniser
                avec Qalimo.
              </p>
            )}
            {provider === BankProvider.LINXO && (
              <p className={"marginB10"}>
                <TextOneOrMany
                  one={"Le compte suivant a été synchronisé avec votre banque."}
                  many={
                    "Les comptes suivants ont été synchronisés avec votre banque."
                  }
                  number={accounts.length}
                />
              </p>
            )}
            <div className={"flex gap10 wrap marginB20"}>
              {accounts.map((account, i) => (
                <BankCheckbox
                  key={i}
                  bank={account}
                  name={"accounts"}
                  value={account.unique_id}
                  disabled={provider === BankProvider.LINXO}
                />
              ))}
            </div>
            <Callout variant="danger">{synchronizeError}</Callout>
            <ModalDialogFooter>
              <FormikSubmitButton
                disabled={values.accounts.length === 0}
                requiresWrite
              >
                {provider === BankProvider.LINXO ? "Fermer" : "Valider"}
              </FormikSubmitButton>
            </ModalDialogFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
